import { Injectable } from "@angular/core";
import { Router } from "@angular/router";
import { Actions, createEffect, ofType } from "@ngrx/effects";
import { Action, Store } from "@ngrx/store";
import { catchError, EMPTY, map, Observable, of, switchMap, tap, withLatestFrom } from "rxjs";
import { LoginPageActions, LoginPageApiActions } from "src/shared/actions-index";
import { LoginService } from "src/shared/services/login.service";
import { AuthInterceptor } from "src/app/interceptors/auth.interceptor";
import { encrypt } from "src/util/cryptography.util";
import { getLoginData, getMfaToken } from "src/shared/state";

@Injectable()
export class LoginApiEffects {
  constructor(
    private router: Router,
    private loginService: LoginService,
    private actions$: Actions,
    private store: Store
  ) { }

  /** Primeira etapa: usuário e senha. */
  login$ = createEffect(() =>
    this.actions$.pipe(
      ofType(LoginPageActions.login),
      switchMap((action) =>
        this.loginService.logIn(action.login.username, action.login.password!).pipe(
          switchMap((result: any) => {
            // Usuário com 2FA: o Bifrost ainda não devolveu o token de sessão.
            // Guarda o token intermediário e espera o código (etapa 2).
            if (result.mfaRequired) {
              return of(LoginPageApiActions.mfaRequired({ mfaToken: result.mfaToken }));
            }
            return this.finishLogin(result, action.login.username!, action.login.password!);
          }),
          catchError((error) => this.onLoginError(error))
        )
      ),
      catchError((error) => this.onLoginError(error))
    )
  );

  /** Segunda etapa: código do app ou código de recuperação. */
  loginMfa$ = createEffect(() =>
    this.actions$.pipe(
      ofType(LoginPageActions.loginMfa),
      withLatestFrom(
        this.store.select(getLoginData),
        this.store.select(getMfaToken)
      ),
      switchMap(([action, loginData, mfaToken]) =>
        this.loginService.verifyMfa(mfaToken!, action.code, action.recoveryCode).pipe(
          switchMap((result: any) =>
            this.finishLogin(result, loginData.username!, loginData.password!)
          ),
          catchError((error) => {
            // BFT012: o token intermediário expirou ou já foi usado -> volta para a etapa 1
            const apiError = error?.error?.errors?.[0];
            if (apiError?.extensions?.code === 'BFT012') {
              this.store.dispatch(LoginPageApiActions.mfaTokenExpired());
            }
            return this.onLoginError(error);
          })
        )
      ),
      catchError((error) => this.onLoginError(error))
    )
  );

  //Faça a ação do tap quando essa action for chamada.
  loginRedirect$ = createEffect(() => {
    return this.actions$.pipe(
      ofType(LoginPageApiActions.loginSuccess),
      tap(() => {
        this.router.navigate(['/map']);
      })
    );
  }, { dispatch: false });

  /**
   * Conclui o login a partir do payload final do Bifrost (mesmo formato com ou sem 2FA).
   * É exatamente o corpo que existia dentro do switchMap do login$.
   */
  private finishLogin(result: any, username: string, password: string): Observable<Action> {
    AuthInterceptor.accessToken = result.token;
    localStorage.setItem('token', result.token);
    localStorage.setItem('userId', result.id);
    localStorage.setItem('username', username);
    localStorage.setItem('expiredPassword', result.expiredPassword);
    localStorage.setItem(
      'lastCompanySelected',
      btoa('Company:' + result.companies[0].id),
    );

    const encryptedPassword = encrypt(password, result.token);
    localStorage.setItem('geoserverPassword', encryptedPassword);

    if (localStorage.getItem('expiredPassword') === 'false') {
      return this.loginService.checkGeoServerAuth(username, password).pipe(
        map(() => {
          this.loginService.showSideBar.emit(true);
          this.loginService.showSideBarUpdateUser.emit(localStorage.getItem('expiredPassword') === 'true');
          return LoginPageApiActions.loginSuccess({
            loggedUserId: result.id,
            token: result.token,
            error: null
          });
        }),
        catchError((error) => this.onLoginError(error))
      );
    }

    this.loginService.showSideBar.emit(true);
    this.loginService.showSideBarUpdateUser.emit(localStorage.getItem('expiredPassword') === 'true');
    return of(LoginPageApiActions.loginSuccess({
      loggedUserId: result.id,
      token: result.token,
      error: null
    }));
  }

  /** Tratamento de erro único (antes repetido três vezes no login$). */
  private onLoginError(error: any): Observable<never> {
    this.store.dispatch(LoginPageApiActions.loginFailed({ error: error.message }));
    this.loginService.showSideBar.emit(false);
    this.loginService.showSideBarUpdateUser.emit(false);
    return EMPTY;
  }
}
